/* ============================================================
   US-Parts24 · Startseite — Hero-Gerät + ruhige Parallaxe
   Wird nur auf der Startseite geladen (snippets/usparts-diagnose-rail).

   ── R511 (19.09.2026): der „Diagnose-Durchlauf" ist abgebaut ─────
   Marcel: „Muss viel besser aussehen … Unbedingt Wert auf allerhöchste
   Performance und Professionalität legen." Ausgezählt: Jeder Abschnitt
   trug seine Schrittnummer DREIMAL — als Stempel oben rechts
   („Schritt 05 · Sortiment ✓"), als Pille vor dem Kicker („● 05") und
   in der Schiene rechts („Diagnose 05/08", am Handy als Chip, der dort
   die Überschriften überdeckte). Dazu ein zweiter Fortschrittsbalken
   neben dem aus usp-motion.js. Das ist genau der Baukasten, den Marcel
   als „billig / KI-mäßig" erkannt hat (Lehre look-scheitert-am-material).
   Weg sind: Schiene, Chip, Stempel, Schrittnummern, zweiter Balken —
   und GSAP + ScrollTrigger (≈ 52 KB gzip), die hier nur noch für eine
   Hero-Parallaxe geladen wurden. Der Prüflauf ([data-usp-run]) steht
   seit R494 nicht mehr auf der Seite.

   R512: Überschriften treten beim ersten Sichtbarwerden Wort für Wort auf.

   Was bleibt:
   · das Diagnose-GERÄT im Hero (Boot-Zeilen, VIN-Zähler, Garage-Gruß) —
     das wollte Marcel ausdrücklich zurück (R509)
   · eine ruhige Parallaxe des Globus: ein transform je Bild, nur solange
     der Hero im Bild ist, ohne Bibliothek

   ── DIE EINE FALLE ─────────────────────────────────────────
   Ab 990 px scrollt `.page-wrapper`, nicht das Fenster. Die Antwort auf
   „wer scrollt gerade?" kommt aus @theme/scroll-container.
   ============================================================ */

import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const inEditor = Boolean(window.Shopify && window.Shopify.designMode);

function stufe() {
  return window.uspMotion ? window.uspMotion.stufe : (reduceMotion ? 'sparsam' : 'voll');
}

function heroFinden() {
  /* Die Wrapper-ID trägt ein Template-Präfix
     (`shopify-section-template--…__hero`) — daher über das Ende suchen. */
  return document.querySelector('[id^="shopify-section-"][id$="__hero"]');
}

/* ------------------------------------------------------------
   1. Das Hero-Gerät
   ------------------------------------------------------------ */

function heroVerdrahten(heroEl) {
  const term = document.querySelector('[data-usp-anschluss]');
  const zeile = term && term.querySelector('[data-usp-anschluss-zeile]');
  const vin = heroEl.querySelector('[data-usp-vin]');
  const zaehler = heroEl.querySelector('[data-usp-vin-zaehler]');
  const gruss = heroEl.querySelector('[data-usp-garage-gruss]');

  /* Boot-Zeilen: der Tester meldet sich. Reine Text-Sequenz, kein Bild. */
  if (term && zeile && stufe() !== 'sparsam') {
    const zeilen = [
      'Tester wird verbunden …',
      'Verbindung steht · OBD bereit',
      'VIN eingeben, um den Lauf zu starten',
    ];
    let i = 0;
    const naechste = () => {
      zeile.textContent = zeilen[i];
      term.classList.toggle('is-live', i >= 1);
      i++;
      if (i < zeilen.length) setTimeout(naechste, i === 1 ? 700 : 900);
    };
    setTimeout(naechste, 250);
  } else if (term) {
    term.classList.add('is-live');
  }

  /* VIN-Zähler: 17 Zeichen sind das Ziel. Der eigentliche Decoder liegt in
     usparts.js (initVinDecoder) — hier nur die Anzeige, kein zweiter Weg. */
  if (vin && zaehler) {
    const aktualisieren = () => {
      const n = vin.value.replace(/\s/g, '').length;
      zaehler.textContent = `${n}/17`;
      zaehler.classList.toggle('is-ok', n === 17);
      zaehler.classList.toggle('is-typing', n > 0 && n < 17);
      if (term && n > 0 && n < 17 && zeile) zeile.textContent = `Lese VIN … ${n}/17`;
      if (term && n === 17 && zeile) zeile.textContent = 'VIN vollständig · Fahrzeug wird erkannt';
    };
    vin.addEventListener('input', aktualisieren);
    aktualisieren();
  }

  /* Garage: wer schon ein Fahrzeug gemerkt hat, wird damit begrüßt.
     Der Speicher gehört usparts.js. */
  if (gruss) {
    let veh = null;
    try { veh = JSON.parse(localStorage.getItem('usp_garage_vehicle_v1') || 'null'); } catch (_) {}
    if (veh && (veh.label || veh.make)) {
      gruss.querySelector('[data-usp-garage-gruss-label]').textContent =
        veh.label || `${veh.make} ${veh.model || ''}`.trim();
      gruss.hidden = false;
      if (term && zeile) setTimeout(() => { zeile.textContent = `Fahrzeug aus der Garage: ${veh.label || veh.make}`; }, 2200);
    }
  }
}

/* ------------------------------------------------------------
   2. Ruhige Parallaxe des Globus — ohne GSAP
   Ein transform je Bild, nur solange der Hero sichtbar ist; die Höhe
   wird zwischengespeichert (R508: kein Layout-Lesen im Takt).
   ------------------------------------------------------------ */

function parallaxe(heroEl) {
  if (reduceMotion || stufe() !== 'voll') return;
  const medium = heroEl.querySelector('.usp-hero__media');
  if (!medium || medium.querySelector('video')) return;

  const lage = () => {
    const desktop = scrollContainerMediaQuery.matches;
    const container = getScrollContainer();
    return { ziel: desktop && container ? container : document, lesen: desktop && container ? () => container.scrollTop : () => window.scrollY };
  };
  let { ziel, lesen } = lage();
  let hoehe = heroEl.offsetHeight || window.innerHeight;
  let zuletzt = -1;
  let angefordert = false;

  const zeichnen = () => {
    angefordert = false;
    if (stufe() !== 'voll') { if (zuletzt !== 0) { medium.style.transform = ''; zuletzt = 0; } return; }
    const oben = lesen();
    if (oben > hoehe && zuletzt >= Math.round(hoehe * 0.18)) return; // längst aus dem Bild
    const y = Math.round(Math.max(0, Math.min(oben, hoehe)) * 0.18);
    if (y === zuletzt) return;
    zuletzt = y;
    medium.style.transform = y ? `translate3d(0, ${y}px, 0)` : '';
  };
  const takt = () => { if (!angefordert) { angefordert = true; requestAnimationFrame(zeichnen); } };

  ziel.addEventListener('scroll', takt, { passive: true });
  window.addEventListener('resize', () => { hoehe = heroEl.offsetHeight || window.innerHeight; takt(); }, { passive: true });
  scrollContainerMediaQuery.addEventListener('change', () => {
    ziel.removeEventListener('scroll', takt);
    ({ ziel, lesen } = lage());
    ziel.addEventListener('scroll', takt, { passive: true });
    takt();
  });
  medium.style.willChange = 'transform';
  takt();
}

/* ------------------------------------------------------------
   3. R512 — Überschriften treten auf: Wort für Wort aus einer Maske
   Nur transform (Compositor), einmal je Überschrift. Ohne JavaScript oder
   bei „Bewegung reduzieren" stehen sie einfach da — der Anfangszustand
   (verschoben) wird erst per Klasse gesetzt, wenn dieses Skript läuft.
   ------------------------------------------------------------ */

function ueberschriften() {
  if (reduceMotion || !('IntersectionObserver' in window)) return;
  const ziele = document.querySelectorAll('#MainContent .usp-section-head .usp-h2, #MainContent .usp-bsg__titel');
  const desktop = scrollContainerMediaQuery.matches;
  const root = desktop ? getScrollContainer() : null;
  const io = new IntersectionObserver((eintraege) => {
    eintraege.forEach((e) => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-da');
      io.unobserve(e.target);
    });
  }, { root: root && root !== document.documentElement ? root : null, threshold: 0.25 });
  ziele.forEach((h) => {
    if (h.classList.contains('usp-auftritt')) return;
    /* nur reine Textüberschriften anfassen */
    if (Array.from(h.childNodes).some((n) => n.nodeType === 1 && n.tagName !== 'BR')) return;
    const woerter = h.textContent.trim().split(/\s+/).filter(Boolean);
    if (!woerter.length) return;
    h.textContent = '';
    woerter.forEach((w, i) => {
      const aussen = document.createElement('span');
      aussen.className = 'usp-wort';
      const innen = document.createElement('span');
      innen.className = 'usp-wort__in';
      innen.style.setProperty('--i', String(i));
      innen.textContent = w;
      aussen.appendChild(innen);
      h.appendChild(aussen);
      if (i < woerter.length - 1) h.appendChild(document.createTextNode(' '));
    });
    h.classList.add('usp-auftritt');
    io.observe(h);
  });
}

function start() {
  if (inEditor) return;
  ueberschriften();
  const hero = heroFinden();
  if (!hero) return;
  heroVerdrahten(hero);
  parallaxe(hero);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', start, { once: true });
} else {
  start();
}
